/**
 * Controls browser back navigation on PDP: when the user arrived via a color swatch link,
 * intercepts the Back button and redirects to the PLP URL. Otherwise Back uses normal history.
 */
function clearRedirectState() {
  sessionStorage.removeItem('PDP_LAST_VISITED_PLP_URL');
  sessionStorage.removeItem('PDP_COLOR_SWATCH_SOURCE_URL');
}

export function controlPdpBrowserBackNavigation() {
  const referrer = document.referrer;
  const referrerIsCollection = referrer && referrer.includes('/collections/');
  if (referrerIsCollection) {
    sessionStorage.setItem('PDP_LAST_VISITED_PLP_URL', referrer);
  }

  const sourceUrl = sessionStorage.getItem('PDP_COLOR_SWATCH_SOURCE_URL');
  const cameViaColorSwatch = referrer && sourceUrl === referrer;
  const collectionUrl = sessionStorage.getItem('PDP_LAST_VISITED_PLP_URL');

  // Fallback when popstate didn't fire: redirect if we're the PDP they left via color swatch
  const currentPageIsColorSwatchSource = sourceUrl === window.location.href;
  if (collectionUrl && currentPageIsColorSwatchSource) {
    clearRedirectState();
    localStorage.setItem('PLP_RESTORE_SCROLL', '1');
    window.location.href = collectionUrl;
    return;
  }

  if (!collectionUrl || !cameViaColorSwatch) return;

  // Chrome requires pushState after a user gesture; otherwise it may skip the entry on Back
  function pushBackEntry() {
    history.pushState({ page: 1 }, '', window.location.href);
  }

  function popstateHandler(event) {
    if (!event) return;
    const url = sessionStorage.getItem('PDP_LAST_VISITED_PLP_URL');
    if (url) {
      clearRedirectState();
      localStorage.setItem('PLP_RESTORE_SCROLL', '1');
      window.location.replace(url);
    }
  }

  function setupBackToCollection() {
    window.removeEventListener('popstate', popstateHandler);
    window.addEventListener('popstate', popstateHandler);
  }

  setupBackToCollection();

  // Push state on first user interaction so Chrome honours it (History Manipulation Intervention)
  document.documentElement.addEventListener('click', () => pushBackEntry(), {
    once: true,
  });
  document.documentElement.addEventListener('keydown', () => pushBackEntry(), {
    once: true,
  });
  document.documentElement.addEventListener('scroll', () => pushBackEntry(), {
    once: true,
  });

  // Re-apply when page is restored from back/forward cache
  window.addEventListener('pageshow', function (event) {
    if (event.persisted && sessionStorage.getItem('PDP_LAST_VISITED_PLP_URL')) {
      setupBackToCollection();
      document.documentElement.addEventListener(
        'click',
        () => pushBackEntry(),
        { once: true }
      );
      document.documentElement.addEventListener(
        'keydown',
        () => pushBackEntry(),
        { once: true }
      );
      document.documentElement.addEventListener(
        'scroll',
        () => pushBackEntry(),
        { once: true }
      );
    }
  });
}

// let colorSwatchesClickListenerAttached = false;

/**
 * Adds click listener to mr-color-swatches. On click stores current PDP URL so next page knows we came via color swatch.
 */
export function initColorSwatchesClickListener() {
  // if (colorSwatchesClickListenerAttached) return;
  // colorSwatchesClickListenerAttached = true;

  const colorSwatchesElement = document.querySelector('mr-color-swatches');
  if (!colorSwatchesElement) return;
  colorSwatchesElement.addEventListener('click', () => {
    sessionStorage.setItem('PDP_COLOR_SWATCH_SOURCE_URL', window.location.href);
  });
}

/**
 * Same-origin check: GET `{origin}{pathname}.js` (Shopify Ajax Product API — small JSON vs full PDP).
 * Does not mutate `targetPageUrl` so `url.href` stays valid for redirect.
 * @see https://shopify.dev/docs/api/ajax/reference/product
 */
async function isSameOriginProductUrlReachable(targetPageUrl) {
  const path = targetPageUrl.pathname.replace(/\/$/, '');
  if (!path || path.endsWith('.js')) return false;

  try {
    const response = await fetch(`${targetPageUrl.origin}${path}.js`, {
      method: 'GET',
      credentials: 'same-origin',
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * When the URL includes a hash (e.g. #navy), replaces the first occurrence of the current
 * PDP color slug (from hidden input `pdp-product-color-name`) in the pathname only (case-
 * insensitive match), preserves other query params but drops `variant`, clears the fragment,
 * then navigates only if the Ajax Product `.js` endpoint for that path responds successfully.
 */
export async function redirectToCorrectProductColour() {
  const rawHash = window.location.hash;
  if (!rawHash || rawHash === '#') return;

  const hashValue = rawHash.slice(1);
  if (!hashValue) return;

  const colorNameInUrl = document
    .querySelector('input[type="hidden"][name="pdp-product-color-name"]')
    ?.value?.toLowerCase();
  if (!colorNameInUrl) return;

  const url = new URL(window.location.href);
  const colorPattern = new RegExp(
    colorNameInUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    'i'
  );
  const newPathname = url.pathname.replace(colorPattern, () => hashValue);
  if (newPathname === url.pathname) return;

  url.pathname = newPathname;
  url.hash = '';
  url.searchParams.delete('variant');

  if (!(await isSameOriginProductUrlReachable(url))) return;

  window.location.replace(url.href);
}
